import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AuthorizationProvider, UserGroup, APPROVER_ACCESSIBLE_ROUTE_GROUPS } from './contexts/AuthorizationContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import { LoadingState } from './components/ui/Spinner';
// Each page is its own chunk, fetched the first time its route renders. A failed
// fetch (a tab left open across a deploy) is handled in main.tsx.
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ApprovalsPage = lazy(() => import('./pages/ApprovalsPage'));
const LOEListPage = lazy(() => import('./pages/LOEListPage'));
const LOEDetailPage = lazy(() => import('./pages/LOEDetailPage'));
const LOEFormPage = lazy(() => import('./pages/LOEFormPage'));
const CountriesPage = lazy(() => import('./pages/CountriesPage'));
const LOETypesPage = lazy(() => import('./pages/LOETypesPage'));
const RoleManagementPage = lazy(() => import('./pages/RoleManagementPage'));
const RateManagementPage = lazy(() => import('./pages/RateManagementPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const SignUpPage = lazy(() => import('./pages/SignUpPage'));
const SignInPage = lazy(() => import('./pages/SignInPage'));
const SSOCallbackPage = lazy(() => import('./pages/SSOCallbackPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const CustomerListPage = lazy(() => import('./pages/CustomerListPage'));
const CustomerFormPage = lazy(() => import('./pages/CustomerFormPage'));
const CustomerDetailPage = lazy(() => import('./pages/CustomerDetailPage'));
const AdminPortalPage = lazy(() => import('./pages/AdminPortalPage'));
const PendingApprovalPage = lazy(() => import('./pages/PendingApprovalPage'));
const ProposalLandingPage = lazy(() => import('./pages/ProposalLandingPage'));
const ProposalSectionPage = lazy(() => import('./pages/ProposalSectionPage'));
const PortfolioListPage = lazy(() => import('./pages/PortfolioListPage'));
const PortfolioDetailPage = lazy(() => import('./pages/PortfolioDetailPage'));
const PortfolioFormPage = lazy(() => import('./pages/PortfolioFormPage'));
const SalesAssetsPage = lazy(() => import('./pages/SalesAssetsPage'));
const SalesAssetViewerPage = lazy(() => import('./pages/SalesAssetViewerPage'));

/**
 * Component to redirect authenticated users away from auth pages
 * Redirects to dashboard (/) when already authenticated
 * Requirements: 10.5, 1.1
 */
const AuthenticatedRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return null; // or a loading spinner
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

/**
 * Handles the SPA deep-link redirect. Direct browser navigations to app routes
 * (e.g. a shared/bookmarked /loes/{id} link opened in a new tab) are bounced by
 * the CloudFront SPARoutingFunction to `/?_r=<original-path>`. This reads `_r`
 * and navigates to the real path once React Router has mounted.
 *
 * It gates rendering of its children until that redirect has been dispatched, so
 * the user never sees a flash of the default "/" route before landing on the
 * deep-linked page. When there is no `_r` param, children render immediately.
 * Auth restore runs in parallel (AuthProvider is mounted above this), so gating
 * here does not delay session restoration.
 */
export function SPARedirectHandler({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('_r');
  const [ready, setReady] = React.useState(!redirectPath);

  React.useEffect(() => {
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
      // Flip ready on the next microtask so the navigation is applied before
      // children render, avoiding a flash of the "/" route.
      Promise.resolve().then(() => setReady(true));
    }
  }, [redirectPath, navigate]);

  if (!ready) {
    return null;
  }

  return <>{children}</>;
}

function App() {
  return (
    <ThemeProvider>
        <ErrorBoundary>
          <NotificationProvider>
            <Router>
              <AuthProvider>
                <AuthorizationProvider>
                  <SPARedirectHandler>
                  <Layout>
                  <Suspense fallback={<LoadingState />}>
                  <Routes>
                    {/* Authentication routes - redirect to home if already authenticated */}
                    <Route 
                      path="/signup" 
                      element={
                        <AuthenticatedRedirect>
                          <SignUpPage />
                        </AuthenticatedRedirect>
                      } 
                    />
                    <Route 
                      path="/verify-email" 
                      element={<VerifyEmailPage />} 
                    />
                    <Route 
                      path="/signin" 
                      element={
                        <AuthenticatedRedirect>
                          <SignInPage />
                        </AuthenticatedRedirect>
                      } 
                    />
                    {/* Entra SSO landing. NOT wrapped in AuthenticatedRedirect:
                        the code exchange has to run even when a stale session is
                        still in storage, otherwise re-authenticating as someone
                        else silently keeps the old session. */}
                    <Route
                      path="/auth/callback"
                      element={<SSOCallbackPage />}
                    />
                    <Route 
                      path="/forgot-password" 
                      element={<ForgotPasswordPage />} 
                    />
                    <Route 
                      path="/reset-password" 
                      element={<ResetPasswordPage />} 
                    />
                    
                    {/* Pending approval page - accessible to all authenticated users */}
                    <Route 
                      path="/pending-approval" 
                      element={
                        <ProtectedRoute>
                          <PendingApprovalPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Profile route - requires authentication */}
                    <Route 
                      path="/profile" 
                      element={
                        <ProtectedRoute>
                          <ProfilePage />
                        </ProtectedRoute>
                      } 
                    />

                    {/* Admin Portal - Admins only */}
                    <Route 
                      path="/admin" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.ADMINS]}>
                          <AdminPortalPage />
                        </ProtectedRoute>
                      } 
                    />

                    {/* Static Content Management - Redirect to Admin Portal */}
                    <Route 
                      path="/admin/static-content" 
                      element={<Navigate to="/admin" replace />}
                    />

                    {/* Full Status (Dashboard) - Users or Admins */}
                    <Route 
                      path="/" 
                      element={
                        <ProtectedRoute requiredGroups={APPROVER_ACCESSIBLE_ROUTE_GROUPS}>
                          <DashboardPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Approvals - dedicated route for the "Approvals" nav entry;
                        reuses the same PendingApprovals surface shown on the
                        Dashboard. Users or Admins (nav entry itself is further
                        gated to approver-group members / Admins). */}
                    <Route
                      path="/approvals"
                      element={
                        <ProtectedRoute requiredGroups={APPROVER_ACCESSIBLE_ROUTE_GROUPS}>
                          <ApprovalsPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* LOE routes - Users or Admins */}
                    <Route 
                      path="/loes" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.USERS, UserGroup.ADMINS]}>
                          <LOEListPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/loe/new" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.USERS, UserGroup.ADMINS]}>
                          <LOEFormPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/loe/:loeId" 
                      element={
                        <ProtectedRoute requiredGroups={APPROVER_ACCESSIBLE_ROUTE_GROUPS}>
                          <LOEDetailPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/loe/:loeId/edit" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.USERS, UserGroup.ADMINS]}>
                          <LOEFormPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Portfolio routes - Users or Admins */}
                    <Route 
                      path="/portfolio" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.USERS, UserGroup.ADMINS]}>
                          <PortfolioListPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/portfolio/new" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.USERS, UserGroup.ADMINS]}>
                          <PortfolioFormPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/portfolio/:offerId" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.USERS, UserGroup.ADMINS]}>
                          <PortfolioDetailPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/portfolio/:offerId/edit" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.USERS, UserGroup.ADMINS]}>
                          <PortfolioFormPage />
                        </ProtectedRoute>
                      } 
                    />

                    {/* Sales Assets library - Users, Admins or Content managers */}
                    <Route
                      path="/sales-assets"
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.USERS, UserGroup.ADMINS, UserGroup.CONTENT_MANAGERS]}>
                          <SalesAssetsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/sales-assets/:assetId"
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.USERS, UserGroup.ADMINS, UserGroup.CONTENT_MANAGERS]}>
                          <SalesAssetViewerPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin-only configuration routes */}
                    <Route 
                      path="/loe-types" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.ADMINS]}>
                          <LOETypesPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/countries" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.ADMINS]}>
                          <CountriesPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/rates" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.ADMINS]}>
                          <RateManagementPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/roles" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.ADMINS]}>
                          <RoleManagementPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Customer routes - Users or Admins */}
                    <Route 
                      path="/customers" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.USERS, UserGroup.ADMINS]}>
                          <CustomerListPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/customers/new" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.USERS, UserGroup.ADMINS]}>
                          <CustomerFormPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/customers/:customerId" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.USERS, UserGroup.ADMINS]}>
                          <CustomerDetailPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/customers/:customerId/edit" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.USERS, UserGroup.ADMINS]}>
                          <CustomerFormPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Proposal routes - Users or Admins */}
                    <Route 
                      path="/proposals/:proposalId/sections/:sectionId" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.USERS, UserGroup.ADMINS]}>
                          <ProposalSectionPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/proposals/:proposalId" 
                      element={
                        <ProtectedRoute requiredGroups={[UserGroup.USERS, UserGroup.ADMINS]}>
                          <ProposalLandingPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* 404 page */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                  </Suspense>
                </Layout>
                  </SPARedirectHandler>
              </AuthorizationProvider>
            </AuthProvider>
          </Router>
        </NotificationProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
